import type { ExperimentWithVerdict } from '@@/api/experiments/implementation-verdict';
import { IconTrophy, IconFlask, IconRocket } from '@tabler/icons-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { resolveWinner } from './variant-selection';
import { describeVerdict, summariseWinner } from './result-summary';

/**
 * Which variant won, on which goal, and by how much.
 *
 * Three states, deliberately three shapes as well as three colours so the distinction
 * survives a colourblind reader and a greyscale print:
 *
 *  - a resolved win, with the goal that carried it and the lift. This is the only state
 *    that puts a variant in the queue.
 *  - a rollout, which is a human decision and shown as such rather than as a result.
 *  - the variant that was merely tested, for a row that is listed for some other reason.
 *
 * Every state's tooltip carries the per-goal summary, because conversions and revenue
 * regularly disagree and a single badge cannot say so.
 */
type WinningVariantCellProps = {
  experiment: ExperimentWithVerdict;
};

export const WinningVariantCell = ({ experiment }: WinningVariantCellProps) => {
  const { variant, source } = resolveWinner(experiment);
  const summary = describeVerdict(experiment.verdict);
  const winner = experiment.verdict?.winner ?? null;

  if (!variant) return <span className="supporting">-</span>;

  const label =
    source === 'result' && winner ? (
      <span className="border-ok-line text-ok rounded-card inline-flex max-w-[14rem] flex-col items-start gap-0.5 border px-2 py-1 text-meta font-medium">
        <span className="flex min-w-0 items-center gap-1.5">
          <IconTrophy className="size-3.5 shrink-0" />
          <span className="truncate" title={variant.name}>
            {variant.name}
          </span>
        </span>
        <span className="num pl-5 font-normal">{summariseWinner(winner)}</span>
      </span>
    ) : source === 'deployment' ? (
      <span className="border-accent-line text-accent rounded-card inline-flex max-w-[14rem] items-center gap-1.5 border px-2 py-1 text-meta font-medium">
        <IconRocket className="size-3.5 shrink-0" />
        <span className="truncate" title={variant.name}>
          {variant.name}
        </span>
      </span>
    ) : (
      <span className="border-line text-dim rounded-card inline-flex max-w-[14rem] items-center gap-1.5 border border-dashed px-2 py-1 text-meta">
        <IconFlask className="size-3.5 shrink-0" />
        <span className="truncate" title={variant.name}>
          {variant.name}
        </span>
      </span>
    );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{label}</TooltipTrigger>
        <TooltipContent className="max-w-xs space-y-1">
          {source === 'deployment' && (
            <p>This variant is rolled out, so it is the one live on the site. What the statistics say:</p>
          )}
          {source === 'tested' && (
            <p>
              No variant won and none is deployed. This is the variant that was tested, and the one the code and
              screenshots refer to.
            </p>
          )}
          {summary.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
